import Vue, { CreateElement } from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import Course from './views/Course.vue';
import Home from './views/Home.vue';
import Classroom from './views/Classroom.vue';
import Lesson from './views/Lesson.vue';
import Library from './views/Library.vue';
import Channel from './views/Channel.vue';
import NotFound from './views/404.vue';
import Schedule from './views/Schedule.vue';
import Navigation from './views/Navigation.vue';
import Analytics from './helpers/analytics';
import Forum from './views/Forum.vue';
import CreateDiscussion from './views/CreateDiscussion.vue';
import Discussion from '@/views/Discussion.vue';
import Notebook from '@/views/Notebook.vue';
import SharedNotebook from '@/views/SharedNotebook.vue';
import Revision from '@/views/Revision.vue';
import RevisionResult from '@/views/RevisionResult.vue';
import PostgradStudyPlanning from '@/views/PostgradStudyPlanning.vue';
import PostgraduateMigration from '@/views/PostgraduateMigration.vue';
import DesConverse from '@/views/DesConverse.vue';
import LinkedinSharingRedirection from '@/views/LinkedinSharingRedirection.vue';
import PostgradDocuments from '@/views/PostgradDocuments.vue';
import ExpiredPostgradContract from '@/views/ExpiredPostgradContract.vue';
import Survey from '@/views/Survey.vue';
import store from './store';

const Live = () => import('./views/Live.vue');

const getDefaultTrackingsByCourseId = () => {
  const {
    getCurrentChimeraCourse,
    getTrialStatusByDescomplicaCourseId: getTrialStatusByDescomplicaCourseIdGetter,
    earlyGraduationTrackings: earlyGraduationTrackingsGetter,
  } = store.getters;
  return {
    ...getTrialStatusByDescomplicaCourseIdGetter(getCurrentChimeraCourse?._id),
    ...earlyGraduationTrackingsGetter(getCurrentChimeraCourse),
  };
};

Vue.use(VueRouter);

const routes: RouteConfig[] = [
  {
    path: '/',
    component: Navigation,
  },
  {
    path: '/caderno-compartilhado/:notebookHash',
    name: 'sharedNotebook',
    component: SharedNotebook,
  },
  {
    path: '/:category/pesquisa/',
    component: Navigation,
    children: [
      {
        path: '/',
        component: NotFound,
      },
      {
        path: ':name',
        name: 'survey',
        component: Survey,
      },
    ],
  },
  {
    path: '/:category/:currentCourse',
    component: {
      render: (c: CreateElement) => c('router-view'),
    },
    children: [
      {
        path: '/',
        component: Navigation,
        children: [
          {
            path: '/',
            name: 'home',
            component: Home,
          },
          {
            path: 'disciplinas',
            name: 'classrooms',
            component: Course,
          },
          {
            path: 'turma/:classroomSlug',
            name: 'classroom',
            component: Classroom,
          },
          {
            path: 'forum',
            name: 'forum',
            component: Forum,
          },
          {
            path: 'cadernos',
            name: 'notebooks',
            component: Notebook,
          },
          {
            path: 'library',
            name: 'library',
            component: Library,
          },
          {
            path: 'forum/canal/:channelId',
            name: 'channel',
            component: Channel,
          },
          {
            path: 'planejamento',
            name: 'postgradStudyPlanning',
            component: PostgradStudyPlanning,
          },
          {
            path: 'documentos',
            name: 'documents',
            component: PostgradDocuments,
          },
          {
            path: 'desconverse',
            name: 'desconverse',
            component: DesConverse,
          },
        ],
      },
      {
        path: 'forum/canal/:channelId/criar-discussao',
        name: 'createDiscussion',
        component: CreateDiscussion,
      },
      {
        path: 'forum/canal/:channelId/discussao/:discussionId',
        name: 'discussion',
        component: Discussion,
      },
      {
        path: 'turma/:classroomSlug/aula/:listSlug',
        name: 'lesson',
        component: Lesson,
        props: true,
      },
      {
        path: 'agendamento',
        name: 'schedule',
        component: Schedule,
      },
      {
        path: 'turma/:classroomSlug/revision/:revisionSlug',
        name: 'revision',
        component: Revision,
      },
      {
        path: 'turma/:classroomSlug/revisionResult/:revisionSlug',
        name: 'revisionResult',
        component: RevisionResult,
      },
      {
        path: 'turma/:classroomSlug/live/:liveId',
        name: 'live',
        component: Live,
      },
      {
        path: 'acesso-expirado',
        name: 'expiredAccess',
        component: ExpiredPostgradContract,
      },
    ],
  },
  {
    path: '/compartilhar',
    name: 'linkedinSharingRedirection',
    component: LinkedinSharingRedirection,
  },
  {
    path: '/migracao',
    name: 'migration',
    component: PostgraduateMigration,
  },
  {
    path: '*',
    name: 'NotFound',
    component: NotFound,
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior(to) {
    if (to.hash) {
      return {
        selector: to.hash,
        behavior: 'smooth',
        offset: { y: 81, x: 0 },
      };
    }

    return { x: 0, y: 0 };
  },
  routes,
});

router.afterEach(async () => {
  await Analytics.registerIfIsInTrial();
  Analytics.trackPageView(getDefaultTrackingsByCourseId());
});

router.beforeEach((to, from, next) => {
  const { currentCourse, isPostgraduateCourse } = store.getters;

  if (!currentCourse) {
    next();
    return;
  }

  if (from.name === 'linkedinSharingRedirection') {
    return;
  }

  const expiredChimeraCourseById = store.getters.expiredChimeraCourseById(
    currentCourse.descomplicaCourseId,
  );

  if (isPostgraduateCourse && expiredChimeraCourseById && to.name !== 'expiredAccess') {
    next({
      name: 'expiredAccess',
      params: {
        category: currentCourse.descomplicaCategorySlug,
        currentCourse: currentCourse.slug,
      },
    });
    return;
  }

  next();
});

export default router;
